import { uid } from 'tools/tools';
import { IDialogItem, IMessagePart, SearchResults } from '../types';

const HTML_IN_MESSAGE_REGEX = /%BEGIN_HTML%([\s\S]+?(?=%END_HTML%))%END_HTML%\s?/gm;
export const JSON_IN_MESSAGE_REGEX = /%BEGIN_JSON%([\s\S]*?)%END_JSON%/gs;
export const THINKING_IN_MESSAGE_REGEX = /%BEGIN_THINKING%([\s\S]*?)%END_THINKING%/gs;
export const JSON_START_MESSAGE_REGEX = /%BEGIN_(JSON|HTML|THINKING)%/g;
const INVALID_REQUEST_REGEX = /^Invalid request:\s*(.+)$/ims;

const SCRIPT_TAG_REGEX = /<script[^>]*>([\s\S]*?)<\/script>/gm;

export const escapeLatexSpecial = (str: string) => str.replace(/(?<!\\)(?:\\\\)*([%&])/g, '\\$1');

export const stripHtmlTags = (text: string): string => {
    return text.replace(/<[^>]*>/g, '');
};

function collectAllBlocks(messageData: string) {
    JSON_IN_MESSAGE_REGEX.lastIndex = 0;
    THINKING_IN_MESSAGE_REGEX.lastIndex = 0;

    const jsonMatches = [...messageData.matchAll(JSON_IN_MESSAGE_REGEX)].map((m) => ({
        match: m,
        kind: 'json' as const,
    }));
    const thinkingMatches = [...messageData.matchAll(THINKING_IN_MESSAGE_REGEX)].map((m) => ({
        match: m,
        kind: 'thinking' as const,
    }));

    return [...jsonMatches, ...thinkingMatches].sort((a, b) => a.match.index! - b.match.index!);
}

export function parseJsonMessage(messageData: string): IMessagePart[] {
    const messageParts: IMessagePart[] = [];
    const blocks = collectAllBlocks(messageData);

    if (blocks.length === 0) {
        const trimmed = messageData.trim();
        if (trimmed) {
            messageParts.push({
                message: trimmed,
                type: 'markdown',
                source: 'backend',
            });
        }
        return messageParts;
    }

    let currentPosition = 0;

    for (const { match, kind } of blocks) {
        const matchStart = match.index!;
        const matchEnd = matchStart + match[0].length;

        if (matchStart > currentPosition) {
            const textBefore = messageData.slice(currentPosition, matchStart).trim();
            if (textBefore.length > 0) {
                messageParts.push({
                    message: textBefore,
                    type: 'markdown',
                    source: 'backend',
                });
            }
        }

        const blockContent = match[1].trim();
        if (blockContent) {
            if (kind === 'json') {
                try {
                    const jsonData = JSON.parse(blockContent);
                    messageParts.push({
                        message: '',
                        type: 'json',
                        source: 'backend',
                        jsonBlocks: [jsonData],
                    });
                } catch (error) {
                    console.error('JSON parsing error:', blockContent, error);
                }
            } else {
                messageParts.push({
                    message: '',
                    type: 'json',
                    source: 'backend',
                    jsonBlocks: [
                        { data_type: 'stream_thinking', content: [{ label: '', type: 'text', value: blockContent }] },
                    ],
                });
            }
        }

        currentPosition = matchEnd;
    }

    if (currentPosition < messageData.length) {
        const textAfter = messageData.slice(currentPosition).trim();
        if (textAfter.length > 0) {
            messageParts.push({
                message: textAfter,
                type: 'markdown',
                source: 'backend',
            });
        }
    }

    return messageParts;
}

const parseTextParts = (text: string, conversationId: string, messageId: string, allowJs: boolean) => {
    const dataParts = text.split(HTML_IN_MESSAGE_REGEX);
    const parts: IMessagePart[] = [];

    for (let i = 0; i < dataParts.length; i++) {
        // MD parts
        if (i % 2 === 0) {
            const valuePart = dataParts[i];

            if (valuePart === '') {
                continue;
            }

            const match = dataParts[i].match(JSON_START_MESSAGE_REGEX);
            if (match) {
                const index = dataParts[i].indexOf(match[0]);

                const beforeMatch = dataParts[i].slice(0, index);
                if (beforeMatch) {
                    parts.push({
                        message: beforeMatch,
                        type: 'markdown',
                        source: 'backend',
                    });
                }

                parts.push({
                    message: match[0] + dataParts[i].slice(index + match[0].length),
                    type: 'json_start',
                    source: 'backend',
                });

                continue;
            }

            const endsWithPartialMarker = dataParts[i].match(
                /%BE?$|%BEGIN_J?S?O?N?$|%END_J?S?O?N?$|%BEGIN_H?T?M?L?$|%BEGIN_T?H?I?N?K?I?N?G?$|%END_T?H?I?N?K?I?N?G?$/,
            );
            const containsPartialMarker = dataParts[i].match(
                /%BEGIN_(?!JSON%|HTML%|THINKING%)|%END_(?!JSON%|HTML%|THINKING%)/,
            );

            if (endsWithPartialMarker || containsPartialMarker) {
                parts.push({
                    message: dataParts[i],
                    type: 'json_start',
                    source: 'backend',
                });
                continue;
            }

            if (valuePart || dataParts.length === 1) {
                parts.push({
                    message: dataParts[i],
                    type: 'markdown',
                    source: 'backend',
                });
            } else {
                parts.push({
                    message: '',
                    type: 'html',
                    source: 'frontend',
                });
            }
        } else {
            let message = dataParts[i].replace(/%BADGE_ID%/g, `ts_${conversationId}_${messageId}_${i}`);

            if (!allowJs) {
                /**
                 * Disable scripts in the message
                 */
                message = message.replace(SCRIPT_TAG_REGEX, `<div style="display: none;">$1</div>`);
            }

            // HTML block parts
            parts.push({
                message,
                type: 'html',
                source: 'backend',
            });
        }
    }
    return parts;
};

export function parseMessage(
    messageData: string,
    conversationId: string,
    messageId: string = uid(),
    allowJs: boolean = false,
): IMessagePart[] {
    const messageParts: IMessagePart[] = [];

    const blocks = collectAllBlocks(messageData);

    if (blocks.length === 0) {
        return parseTextParts(messageData, conversationId, messageId, allowJs);
    }

    let currentPosition = 0;
    for (const { match, kind } of blocks) {
        const matchStart = match.index!;
        const matchEnd = matchStart + match[0].length;

        if (matchStart > currentPosition) {
            const textBefore = messageData.slice(currentPosition, matchStart).trim();
            if (textBefore.length > 0) {
                const textParts = parseTextParts(textBefore, conversationId, messageId, allowJs);
                messageParts.push(...textParts);
            }
        }

        const blockContent = match[1].trim();
        if (blockContent) {
            if (kind === 'json') {
                try {
                    const jsonData = JSON.parse(blockContent);
                    messageParts.push({
                        message: '',
                        type: 'json',
                        source: 'backend',
                        jsonBlocks: [jsonData],
                    });
                } catch (error) {
                    console.error('JSON parse error:', error, blockContent);
                }
            } else {
                messageParts.push({
                    message: '',
                    type: 'json',
                    source: 'backend',
                    jsonBlocks: [
                        { data_type: 'stream_thinking', content: [{ label: '', type: 'text', value: blockContent }] },
                    ],
                });
            }
        }

        currentPosition = matchEnd;
    }

    if (currentPosition < messageData.length) {
        const textAfter = messageData.slice(currentPosition).trim();
        if (textAfter.length > 0) {
            const textParts = parseTextParts(textAfter, conversationId, messageId, allowJs);
            messageParts.push(...textParts);
        }
    }

    return messageParts;
}

export function processParts(parts: IMessagePart[]): IMessagePart[] {
    const processedParts: IMessagePart[] = [];

    const containStartJson = parts.some((part) => part.type === 'json_start');

    if (!containStartJson) {
        return parts;
    }

    if (containStartJson) {
        return parts.slice(
            0,
            parts.findIndex((part) => part.type === 'json_start'),
        );
    }

    return processedParts;
}

export function findPreviousRequest(dialog: IDialogItem[], currentIndex: number): IDialogItem | null {
    for (let i = currentIndex - 1; i >= 0; i--) {
        if (dialog[i].type === 'request') {
            return dialog[i];
        }
    }
    return null;
}

export function findResolution(item: IDialogItem): string {
    let resolution = '';
    let result: SearchResults | null = null;

    if (item.type === 'response') {
        const parts = item.parts || [];

        for (const part of parts) {
            if (part.type === 'json' && part.jsonBlocks) {
                for (const block of part.jsonBlocks) {
                    if (block.data_type === 'search_results') {
                        result = block as SearchResults;

                        break;
                    }
                }
            }

            if (part.type === 'markdown') {
                resolution = resolution + part.message;
            }
        }
    }

    if (result) {
        resolution = result.search_criteria_summary_phrase;
    }

    return resolution;
}

export interface ParsedMarkdownTable {
    data_type: 'table';
    data: Array<Record<string, any>>;
    sql: string;
    htmlBefore?: string;
    htmlAfter?: string;
}

export function parseMarkdownTable(html: string): ParsedMarkdownTable[] {
    const tables: ParsedMarkdownTable[] = [];
    const tableRegex = /<table>([\s\S]*?)<\/table>/gi;
    let lastIndex = 0;
    let match;

    while ((match = tableRegex.exec(html)) !== null) {
        const tableHtml = match[0];
        const tableStart = match.index;
        const tableEnd = tableRegex.lastIndex;

        const parser = new DOMParser();
        const doc = parser.parseFromString(tableHtml, 'text/html');
        const table = doc.querySelector('table');

        if (!table) continue;

        const headers: string[] = [];
        const headerRow = table.querySelector('thead tr');

        if (headerRow) {
            headerRow.querySelectorAll('th').forEach((th) => {
                headers.push(th.textContent?.trim() || '');
            });
        }

        if (headers.length === 0) continue;

        const data: Array<Record<string, any>> = [];
        const tbody = table.querySelector('tbody');

        if (tbody) {
            tbody.querySelectorAll('tr').forEach((tr) => {
                const row: Record<string, any> = {};
                tr.querySelectorAll('td').forEach((td, index) => {
                    if (index < headers.length) {
                        row[headers[index]] = td.textContent?.trim() || '';
                    }
                });
                if (Object.keys(row).length > 0) {
                    data.push(row);
                }
            });
            if (data.length > 0) {
                const htmlBefore = lastIndex < tableStart ? html.substring(lastIndex, tableStart) : undefined;

                tables.push({
                    data_type: 'table',
                    data,
                    sql: '',
                    htmlBefore,
                });

                lastIndex = tableEnd;
            }
        }
    }

    if (tables.length > 0) {
        const lastTable = tables[tables.length - 1];
        if (lastIndex < html.length) {
            lastTable.htmlAfter = html.substring(lastIndex);
        }
    }

    return tables;
}
